import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { updatePersona } from '../../services/apiService';
import styles from './NarrativeModal.module.css';

const EditableListItem = ({ item, index, onChange, onDelete, fields }) => {
  return (
    <div className={styles.listItem}>
      {fields.map((field) => (
        <div key={field.name} className={styles.formGroup}>
          <label htmlFor={`${field.name}-${index}`}>{field.label}</label>
          <input
            type="text"
            id={`${field.name}-${index}`}
            name={field.name}
            value={item[field.name] || ''}
            onChange={(e) => onChange(index, e)}
            placeholder={field.placeholder}
          />
        </div>
      ))}
      <button onClick={() => onDelete(index)} className={styles.deleteButton}>
        &times;
      </button>
    </div>
  );
};

const NarrativeModal = ({ digitalLegacy, onClose, onSave }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [activeTab, setActiveTab] = useState('narrative');

  const [narrativeData, setNarrativeData] = useState(
    digitalLegacy.narrativeData || {}
  );
  const [expertiseData, setExpertiseData] = useState(
    digitalLegacy.expertiseData || { workHistory: [], education: [] }
  );

  const canSave =
    narrativeData.biography && narrativeData.biography.trim() !== '';
  const [isSaving, setIsSaving] = useState(false);

  const [newHobby, setNewHobby] = useState('');

  const handleNarrativeChange = (e) => {
    const { name, value } = e.target;
    setNarrativeData((prev) => ({ ...prev, [name]: value }));
  };

  const handleExpertiseChange = (e) => {
    const { name, value } = e.target;
    setExpertiseData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddHobby = () => {
    if (newHobby.trim() === '') return;
    const currentHobbies = narrativeData.hobbiesAndInterests || [];
    if (currentHobbies.includes(newHobby.trim())) return;

    setNarrativeData((prev) => ({
      ...prev,
      hobbiesAndInterests: [...currentHobbies, newHobby.trim()],
    }));
    setNewHobby('');
  };

  const handleRemoveHobby = (indexToRemove) => {
    setNarrativeData((prev) => ({
      ...prev,
      hobbiesAndInterests: prev.hobbiesAndInterests.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  const handleWorkChange = (index, e) => {
    const { name, value } = e.target;
    const updatedHistory = [...(expertiseData.workHistory || [])];
    updatedHistory[index] = { ...updatedHistory[index], [name]: value };
    setExpertiseData((prev) => ({ ...prev, workHistory: updatedHistory }));
  };

  const addWorkItem = () => {
    setExpertiseData((prev) => ({
      ...prev,
      workHistory: [...(prev.workHistory || []), {}],
    }));
  };

  const deleteWorkItem = (index) => {
    setExpertiseData((prev) => ({
      ...prev,
      workHistory: prev.workHistory.filter((_, i) => i !== index),
    }));
  };

  const handleEducationChange = (index, e) => {
    const { name, value } = e.target;
    const updatedEducation = [...(expertiseData.education || [])];
    updatedEducation[index] = { ...updatedEducation[index], [name]: value };
    setExpertiseData((prev) => ({ ...prev, education: updatedEducation }));
  };

  const addEducationItem = () => {
    setExpertiseData((prev) => ({
      ...prev,
      education: [...(prev.education || []), {}],
    }));
  };

  const deleteEducationItem = (index) => {
    setExpertiseData((prev) => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const payload = {
        narrativeData,
        expertiseData,
      };
      await updatePersona(digitalLegacy.personaId, payload, accessToken);
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save data:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={() => onClose()}>
          &times;
        </button>
        <h3>Narrative & Experience</h3>
        <p className={styles.description}>
          Flesh out your Digital Passport with your personal story and
          professional background. This data gives your AI a richer
          understanding of who you are.
        </p>

        <div className={styles.tabNav}>
          <button
            className={activeTab === 'narrative' ? styles.active : ''}
            onClick={() => setActiveTab('narrative')}
          >
            Personal Narrative
          </button>
          <button
            className={activeTab === 'experience' ? styles.active : ''}
            onClick={() => setActiveTab('experience')}
          >
            Professional Experience
          </button>
        </div>

        <div className={styles.tabContent}>
          {activeTab === 'narrative' && (
            <>
              <div className={styles.formGroup}>
                <label htmlFor="biography">Biography / Life Story</label>
                <textarea
                  id="biography"
                  name="biography"
                  rows="6"
                  value={narrativeData.biography || ''}
                  onChange={handleNarrativeChange}
                  placeholder="Tell your story. Where are you from? What are the key moments that have shaped you? Add as much or as little data as you want!"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Hobbies & Interests</label>
                <div className={styles.hobbyGrid}>
                  {(narrativeData.hobbiesAndInterests || []).map(
                    (hobby, index) => (
                      <div key={index} className={styles.hobbyPill}>
                        {hobby}
                        <button
                          onClick={() => handleRemoveHobby(index)}
                          className={styles.removeHobbyButton}
                        >
                          &times;
                        </button>
                      </div>
                    )
                  )}
                </div>
                <div className={styles.addHobbyInputGroup}>
                  <input
                    type="text"
                    value={newHobby}
                    onChange={(e) => setNewHobby(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      (e.preventDefault(), handleAddHobby())
                    }
                    placeholder="e.g., Hiking"
                  />
                  <button
                    onClick={handleAddHobby}
                    className="action-button secondary"
                  >
                    Add
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'experience' && (
            <>
              <div className={styles.formGroup}>
                <label htmlFor="professionalSummary">
                  Professional Summary
                </label>
                <textarea
                  id="professionalSummary"
                  name="professionalSummary"
                  rows="4"
                  value={expertiseData.professionalSummary || ''}
                  onChange={handleExpertiseChange}
                  placeholder="A brief summary of your career, skills, and professional goals."
                />
              </div>

              <div className={styles.listSection}>
                <div className={styles.listHeader}>
                  <label>Work History</label>
                  <button onClick={addWorkItem} className={styles.addButton}>
                    + Add Role
                  </button>
                </div>
                {(expertiseData.workHistory || []).map((item, index) => (
                  <EditableListItem
                    key={index}
                    item={item}
                    index={index}
                    onChange={handleWorkChange}
                    onDelete={deleteWorkItem}
                    fields={[
                      {
                        name: 'title',
                        label: 'Title',
                        placeholder: 'e.g., Principal Engineer',
                      },
                      {
                        name: 'company',
                        label: 'Company',
                        placeholder: 'e.g., SoulstoreAI',
                      },
                    ]}
                  />
                ))}
              </div>

              <div className={styles.listSection}>
                <div className={styles.listHeader}>
                  <label>Education</label>
                  <button
                    onClick={addEducationItem}
                    className={styles.addButton}
                  >
                    + Add Education
                  </button>
                </div>
                {(expertiseData.education || []).map((item, index) => (
                  <EditableListItem
                    key={index}
                    item={item}
                    index={index}
                    onChange={handleEducationChange}
                    onDelete={deleteEducationItem}
                    fields={[
                      {
                        name: 'institution',
                        label: 'Institution',
                        placeholder: 'e.g., MIT',
                      },
                      {
                        name: 'degree',
                        label: 'Degree',
                        placeholder: 'e.g., B.S. in Computer Science',
                      },
                    ]}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className={styles.modalActions}>
          <button onClick={onClose} className="action-button secondary">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="action-button primary"
            disabled={isSaving || !canSave}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NarrativeModal;
